import { useMemo, useState } from 'react';
import { VERDICT_CSS, VERDICT_LABEL, type TestReview, type Verdict } from '@sr-test-wizard/shared';

interface Props {
  tests: TestReview[];
  running: boolean;
  playingKey: string | null;
  queuedKeys: string[];
  playVerdicts: Record<string, string>;
  onPlay: (testKeys: string[]) => void;
  onStopQueue: () => void;
}

const SHORT_VERDICT: Record<Verdict, string> = {
  PASS: 'Pass',
  FAIL: 'Fail',
  NEEDS_IN_GAME: 'In-Game',
  N_A: 'N/A',
};

function effectiveVerdict(t: TestReview): Verdict | null {
  return t.tester_verdict ?? t.ai_verdict ?? null;
}

/**
 * TEXEC play queue: the same rows Phase 5 loaded, filtered to "In-Game" by
 * default. Selected tests are played live in order on the open session.
 */
export function TestQueuePanel({
  tests,
  running,
  playingKey,
  queuedKeys,
  playVerdicts,
  onPlay,
  onStopQueue,
}: Props) {
  const [inGameOnly, setInGameOnly] = useState(true);
  const [selected, setSelected] = useState<string[]>([]); // in play order

  const visible = useMemo(
    () => (inGameOnly ? tests.filter((t) => effectiveVerdict(t) === 'NEEDS_IN_GAME') : tests),
    [tests, inGameOnly],
  );

  const queueActive = Boolean(playingKey) || queuedKeys.length > 0;

  const toggle = (key: string) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const move = (key: string, dir: -1 | 1) =>
    setSelected((prev) => {
      const i = prev.indexOf(key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const selectInGame = () =>
    setSelected(tests.filter((t) => effectiveVerdict(t) === 'NEEDS_IN_GAME').map((t) => t.test_key));

  if (tests.length === 0) {
    return (
      <div className="card">
        <h3 style={{ margin: '0 0 8px' }}>TEXEC tests</h3>
        <p className="muted small" style={{ margin: 0 }}>
          No tests loaded for this run — load tests in TEXEC execution (Phase 5) first.
        </p>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0 }}>TEXEC tests</h3>
        <label className="muted small row" style={{ gap: 4, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={inGameOnly}
            onChange={(e) => setInGameOnly(e.target.checked)}
          />
          In-Game only
        </label>
        <span className="spacer" />
        <button type="button" className="btn small" onClick={selectInGame} disabled={queueActive}>
          Select in-game
        </button>
        <button
          type="button"
          className="btn small btn-success"
          disabled={!running || queueActive || selected.length === 0}
          title={!running ? 'Start a session first' : undefined}
          onClick={() => onPlay(selected)}
        >
          Play selected ({selected.length})
        </button>
        {queueActive && (
          <button type="button" className="btn small btn-danger" onClick={onStopQueue}>
            Stop queue
          </button>
        )}
      </div>

      <p className="muted small" style={{ margin: '0 0 8px' }}>
        The live-play bot receives the full Xray description, every step, and
        Settings → Test guidance — not only the title shown here.
      </p>

      {queueActive && (
        <p className="muted small" style={{ margin: '0 0 8px' }}>
          {playingKey ? `Playing ${playingKey} now` : 'Queue starting…'}
          {queuedKeys.length > 0 && ` — next: ${queuedKeys.join(', ')}`}
        </p>
      )}

      {visible.length === 0 ? (
        <p className="muted small" style={{ margin: 0 }}>
          No tests with an In-Game verdict. Toggle the filter to queue any loaded test.
        </p>
      ) : (
        <div className="look-at-that-queue">
          {visible.map((t) => {
            const verdict = effectiveVerdict(t);
            const order = selected.indexOf(t.test_key);
            const isPlaying = playingKey === t.test_key;
            const isQueued = queuedKeys.includes(t.test_key);
            const live = t.in_game_play?.verdict ?? (playVerdicts[t.test_key] as Verdict | undefined);
            return (
              <div
                key={t.test_key}
                className={`look-at-that-queue-row${isPlaying ? ' playing' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={order >= 0}
                  disabled={queueActive}
                  onChange={() => toggle(t.test_key)}
                />
                <span className="mono small" style={{ minWidth: 84 }}>{t.test_key}</span>
                <span className="small" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.test_summary}>
                  {t.test_summary}
                </span>
                {verdict && (
                  <span className="row small" style={{ gap: 4 }} title={VERDICT_LABEL[verdict]}>
                    <span className={`dot ${VERDICT_CSS[verdict]}`} />
                    {SHORT_VERDICT[verdict]}
                  </span>
                )}
                {isPlaying && <span className="badge small">playing…</span>}
                {!isPlaying && isQueued && <span className="badge small">queued</span>}
                {live && (
                  <span
                    className="badge small"
                    title={t.in_game_play?.rationale ?? 'Live play verdict'}
                  >
                    live: {SHORT_VERDICT[(live as Verdict)] ?? live}
                  </span>
                )}
                {order >= 0 && !queueActive && (
                  <span className="row" style={{ gap: 2 }}>
                    <span className="badge small mono">#{order + 1}</span>
                    <button type="button" className="btn-ghost small" title="Earlier" onClick={() => move(t.test_key, -1)}>
                      ↑
                    </button>
                    <button type="button" className="btn-ghost small" title="Later" onClick={() => move(t.test_key, 1)}>
                      ↓
                    </button>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
